import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { EntityTreeNode } from '../types/order.types';

/**
 * The one reactive source for "which account am I in" (DV-3013, Phase C of
 * DV-3007 — spec §3.1).
 *
 * Before this slice, six localStorage keys were read directly by 20 files.
 * That worked only because a switch reloaded the document, which re-ran every
 * mount-time read; spec §2.2 is the argument for why removing the reload makes
 * a single source mandatory rather than merely tidier.
 *
 * localStorage is still written, but as this slice's SERIALIZATION FORMAT
 * (spec D4) — the key names and encodings are unchanged, so a session already
 * logged in when this deploys keeps working and a debugger shows the same
 * things. `entity-scope-persistence.ts` is its only writer.
 *
 * Two things deliberately live OUTSIDE this slice:
 *   - `currentContext`, which `processTokenData` rewrites from the token on
 *     every refresh, and which is display-only (spec D3).
 *   - `role`, which is identity rather than scope (spec D8).
 */
export interface EntityScopeState {
  /**
   * Whether an entity has been CHOSEN in this tab — the question the presence
   * of `selectedOrg` used to stand in for (DV-3012 D4, discharged in spec
   * §3.3). Distinct from `entityId`, which mirrors the token claim: after
   * `initiateOrgSwitch()` the session still HAS a claim but has un-chosen it.
   */
  status: 'unscoped' | 'scoped';
  entityId: string | null;
  entityName: string | null;
  parentEntityId: string | null;
  /**
   * The USER (`sub`) this tab's scope was written under, or null for a session
   * persisted before DV-3573.
   *
   * <p>Review finding 3. This started as the entity claim, to separate "this
   * tab is behind the token" from "this tab navigated somewhere the token does
   * not name". Once a switch stops refreshing the token, the first state can
   * only be produced by something outside this UI — in practice an old-bundle
   * tab during the deploy window — and treating it as staleness left that tab
   * silently scoped by the token forever, with the screen naming one account
   * and the data coming from another.
   *
   * <p>So the question narrowed to the one that still matters: has a DIFFERENT
   * PERSON taken over this token? One token is shared by every tab, so a tab
   * still holding the previous user's selection belongs to a dead session, and
   * the right answer is to reload it rather than re-point it.
   *
   * <p>Read only through `userChangedSince`.
   */
  subAtWrite: string | null;
  /**
   * The token's scope ROOT when this scope was written (DV-3421).
   *
   * <p>A cross-tree switch re-anchors the shared token. Every other tab is
   * then holding a selection the new token cannot reach, and would be refused
   * silently on every request. This stamp is what lets a sync reconcile see
   * that — the same shape, and the same reason, as `subAtWrite`.
   */
  rootAtWrite: string | null;

  /**
   * The caller's realm role IN THIS ACCOUNT, e.g. `CUSTOMER_ADMIN` — the form
   * every RBAC reader wants, so `getScopeRole()` returns it as is. Null when
   * the account's org row carries no role the UI maps (see `switchEntity`).
   *
   * <p>DV-3573 review finding 1. Role used to be re-derived from the token on
   * every refresh, which worked only because a switch refreshed the token. It
   * no longer does, so a user who is CustomerAdmin in A and CustomerUser in B
   * would keep A's role while viewing B — including in App.tsx's route guards.
   * The role travels with the scope now, for the same reason the entity does.
   */
  role: string | null;

  /** null = no signal. Only an explicit `false` means "no watermark on file". */
  hasWatermark: boolean | null;
  skipCheckout: boolean;
  entityTree: EntityTreeNode | null;
}

export interface ScopeChangedPayload {
  /** The caller's realm role in the account being switched to, or null. */
  role: string | null;
  /**
   * The user (`sub`) of the token this switch completes under. Passed in
   * rather than read inside the reducer, which must stay pure —
   * `tokenSubject()` reads localStorage.
   */
  subAtWrite: string | null;
  /** The token's scope root at write time (DV-3421); same reason as above. */
  rootAtWrite: string | null;
  entityId: string;
  entityName: string;
  parentEntityId: string;
  hasWatermark: boolean | null;
  skipCheckout: boolean;
}

/**
 * Has a DIFFERENT PERSON taken over the shared token since this scope was
 * written? DV-3573.
 *
 * <p>THE predicate for "this tab's scope no longer belongs to the token". The
 * request header (`entityScopeHeaderValue`) and cross-tab reconciliation
 * (`reconcileScope()`) both ask it and must get the same answer, so they share
 * this rather than each spelling the comparison.
 *
 * <p>A null `subAtWrite` is a session persisted before this shipped: nothing
 * to compare, so nothing has changed hands. The backend still refuses a header
 * the caller may not use, so the worst case is a 403 rather than wrong data.
 */
export const userChangedSince = (
  sub: string,
  scope: Pick<EntityScopeState, 'subAtWrite'>,
): boolean => scope.subAtWrite !== null && sub !== scope.subAtWrite;

/**
 * The stamp for a scope the backend has said its token cannot reach (DV-3421
 * review round 6, item A). Not a UUID, so it equals no token's root: every
 * reader treats it as "the root has moved", and the persisted-scope read
 * discards it — which is what makes the reload that follows converge.
 */
export const UNREACHABLE_ROOT = 'unreachable';

/**
 * Has the shared token's scope ROOT moved since this scope was written?
 * DV-3421.
 *
 * <p>The companion of {@link userChangedSince}, for the same reason it is one
 * function: the persisted-scope read (which discards such a scope) and
 * cross-tab reconciliation (which acts on it) must agree about every input,
 * including the null ones — review round 5, item 8, found the comparison
 * hand-copied in three places, where one null-handling tweak would have made
 * them disagree.
 *
 * <p>Both sides must be known: a null `rootAtWrite` is a scope persisted
 * before the stamp existed, and a null `root` is a token whose root cannot be
 * read (none, or a claim the backend would refuse — see `scopeRootOfClaims`).
 * Neither is evidence of a move — but a null stamp is not evidence of NO move
 * either (review round 6, item A): `reconcileScope` asks the backend about
 * such a scope and stamps it, so this answers for it from then on.
 */
export const rootMovedSince = (
  root: string | null,
  scope: Pick<EntityScopeState, 'rootAtWrite'>,
): boolean => scope.rootAtWrite !== null && root !== null && root !== scope.rootAtWrite;

export const initialEntityScopeState: EntityScopeState = {
  status: 'unscoped',
  subAtWrite: null,
  rootAtWrite: null,
  role: null,
  entityId: null,
  entityName: null,
  parentEntityId: null,
  hasWatermark: null,
  skipCheckout: false,
  entityTree: null,
};

const entityScopeSlice = createSlice({
  name: 'entityScope',
  initialState: initialEntityScopeState,
  reducers: {
    /** Bootstrap only: adopt what was persisted. Spec §3.2 / D2. */
    scopeHydrated: (_state, action: PayloadAction<EntityScopeState>) => action.payload,

    /** A completed switch. */
    scopeChanged: (state, action: PayloadAction<ScopeChangedPayload>) => {
      state.status = 'scoped';
      // DV-3573: stamp the user this scope was written under, so a tab can
      // tell a different person taking over the shared token from its own
      // deliberate navigation.
      state.subAtWrite = action.payload.subAtWrite;
      // DV-3421: and the root it was written under, so a tab can tell that a
      // cross-tree switch elsewhere re-anchored the shared token.
      state.rootAtWrite = action.payload.rootAtWrite;
      state.role = action.payload.role;
      state.entityId = action.payload.entityId;
      state.entityName = action.payload.entityName;
      state.parentEntityId = action.payload.parentEntityId;
      state.hasWatermark = action.payload.hasWatermark;
      state.skipCheckout = action.payload.skipCheckout;
      // The cached tree belongs to the PREVIOUS parent. A stale tree is worse
      // than a missing one — readers degrade gracefully and the header refetches
      // (DV-3011 D3).
      state.entityTree = null;
    },

    /**
     * "Switch Organization": un-choose the current account without claiming a
     * new one. Mirrors `initiateOrgSwitch()`, which clears `selectedOrg` and
     * `entityTree` and nothing else — the claim survives, which is why
     * `entityId` does too.
     */
    scopeCleared: (state) => {
      state.status = 'unscoped';
      state.entityName = null;
      state.entityTree = null;
    },

    /** SSE rename. Display only — no scope has moved. */
    entityRenamed: (state, action: PayloadAction<string>) => {
      state.entityName = action.payload;
    },

    /**
     * DV-3421 review round 6, item A: give a scope persisted before the root
     * stamp existed the stamp it never got — the token's root once the backend
     * has confirmed the selection is reachable under it, or
     * {@link UNREACHABLE_ROOT} once it has said it is not. Only ever fills a
     * null: a stamp written by a switch is never overwritten.
     */
    scopeRootStamped: (state, action: PayloadAction<string>) => {
      if (state.status === 'scoped' && state.rootAtWrite === null) {
        state.rootAtWrite = action.payload;
      }
    },

    entityTreeLoaded: (state, action: PayloadAction<EntityTreeNode | null>) => {
      state.entityTree = action.payload;
    },
  },
});

export const {
  scopeHydrated,
  scopeChanged,
  scopeCleared,
  entityRenamed,
  entityTreeLoaded,
  scopeRootStamped,
} = entityScopeSlice.actions;

/**
 * Selectors take a structurally-typed root rather than `RootState`, so this
 * module keeps no import edge to `store.ts` (see get-store.ts). The store's
 * real state satisfies it.
 */
export interface ScopeRoot {
  entityScope: EntityScopeState;
}

export const selectEntityScope = (s: ScopeRoot): EntityScopeState => s.entityScope;
export const selectSelectedEntityId = (s: ScopeRoot): string | null => s.entityScope.entityId;
export const selectSelectedOrgName = (s: ScopeRoot): string | null => s.entityScope.entityName;
export const selectParentEntityId = (s: ScopeRoot): string | null => s.entityScope.parentEntityId;
export const selectIsScoped = (s: ScopeRoot): boolean => s.entityScope.status === 'scoped';
export const selectSkipCheckout = (s: ScopeRoot): boolean => s.entityScope.skipCheckout;
export const selectEntityTree = (s: ScopeRoot): EntityTreeNode | null => s.entityScope.entityTree;

/**
 * Only an explicit `false` counts as "no watermark on file"; unknown/absent
 * reads as available so the watermark toggle is never over-disabled. This is
 * `useOrgWatermarkAvailable`'s rule, preserved verbatim.
 *
 * `hasWatermark` describes the CHOSEN account, and `scopeCleared` leaves it
 * behind, so with no account chosen it is unknown whatever the field says.
 * Kept for any unscoped reader. The internal asset route no longer is one:
 * it reads the property's own account (`useWorkspaceAccount`, DV-2849 PR 2).
 */
export const selectHasWatermarkAvailable = (s: ScopeRoot): boolean =>
  s.entityScope.status !== 'scoped' || s.entityScope.hasWatermark !== false;

export default entityScopeSlice.reducer;
